import { cn } from "@/lib/utils";

/** The four grades the legacy `JobSuccess_rate` used — thresholds unchanged. */
export function successRateGrade(rate: number) {
  if (rate < 30) return "poor" as const;
  if (rate < 50) return "low" as const;
  if (rate < 70) return "mid" as const;
  return "high" as const;
}

const GRADE_TEXT = {
  poor: "text-rate-poor",
  low: "text-rate-low",
  mid: "text-rate-mid",
  high: "text-rate-high",
} as const;

/**
 * Job success-rate percentage (confirmed / completed tasks), colour-graded.
 *
 * Colours come from the `--rate-*` ramp, NOT `--success`/`--warning`/`--info`/
 * `--destructive`. Those are tuned as solid fills behind their paired
 * `-foreground`; rendered as text on `--card` they measured 3.18:1 (success),
 * 4.30:1 (info) and 2.14:1 (warning) in light mode, and 3.78:1 (destructive) in
 * dark — all under WCAG AA. The ramp is re-picked per theme to clear it (worst
 * case 5.37:1). Grades and thresholds are unchanged.
 *
 * Returns null when there are no completed tasks to rate.
 */
export function JobSuccessRate({
  completedTasks,
  confirmedTasks,
  className,
}: {
  completedTasks: number;
  confirmedTasks: number;
  className?: string;
}) {
  if (completedTasks <= 0) return null;
  const rate = (confirmedTasks / completedTasks) * 100;
  return (
    <span
      className={cn(
        "font-medium tabular-nums",
        GRADE_TEXT[successRateGrade(rate)],
        className,
      )}
    >
      {rate.toFixed(0)}%
    </span>
  );
}
